import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../auth/AuthContext';
import { setPassword } from '../api/auth';
import { updateDisplayName, deleteAccount, updateEmailPreferences } from '../api/me';
import { updateLeaderboardName, randomiseLeaderboardName } from '../api/leaderboard';
import { apiErrorMessage } from '../api/client';
import { AuthInput } from '../components/auth/AuthInput';
import { PasswordInput } from '../components/auth/PasswordInput';

export default function Settings() {
  const { user, loading, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmDelete, setConfirmDelete] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [lbName, setLbName] = useState<string | null>(null);

  if (loading) {
    return null;
  }

  if (!user) {
    return (
      <section data-testid="page-settings">
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="mt-4 text-stone-600">
          <Link to="/login" className="font-semibold text-emerald-700 underline">
            Sign in
          </Link>{' '}
          to manage your account.
        </p>
      </section>
    );
  }

  async function onSaveName(e: FormEvent) {
    e.preventDefault();
    const name = (displayName ?? user?.display_name ?? '').trim();
    if (!name) return;
    setBusy('name');
    try {
      const updated = await updateDisplayName(name);
      setUser(updated);
      toast.success('Display name updated');
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't update the name"));
    } finally {
      setBusy(null);
    }
  }

  async function onSaveLeaderboardName(e: FormEvent) {
    e.preventDefault();
    const name = (lbName ?? user?.leaderboard_name ?? '').trim();
    if (!name) return;
    setBusy('lb-name');
    try {
      const updated = await updateLeaderboardName(name);
      setUser(updated);
      setLbName(null);
      toast.success('Leaderboard name updated');
    } catch (err) {
      toast.error(apiErrorMessage(err, "That name isn't available — try another"));
    } finally {
      setBusy(null);
    }
  }

  async function onRandomise() {
    setBusy('lb-random');
    try {
      const updated = await randomiseLeaderboardName();
      setUser(updated);
      setLbName(null);
      toast.success(`You are now ${updated.leaderboard_name}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't randomise the name"));
    } finally {
      setBusy(null);
    }
  }

  async function onToggleReminders(next: boolean) {
    setBusy('reminders');
    try {
      // checked = reminders ON = opt_out false
      const updated = await updateEmailPreferences({ email_opt_out: !next });
      setUser(updated);
      toast.success(next ? 'Reminders on' : 'Reminders off');
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't update reminders"));
    } finally {
      setBusy(null);
    }
  }

  async function onToggleDigest(next: boolean) {
    setBusy('digest');
    try {
      const updated = await updateEmailPreferences({ results_digest_opt_out: !next });
      setUser(updated);
      toast.success(next ? 'Results email on' : 'Results email off');
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't update the results email"));
    } finally {
      setBusy(null);
    }
  }

  async function onSetPassword(e: FormEvent) {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('Password needs at least 8 characters');
      return;
    }
    if (confirmPassword !== newPassword) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy('password');
    try {
      await setPassword(newPassword);
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password set — you can sign in with it from now on');
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't set the password"));
    } finally {
      setBusy(null);
    }
  }

  async function onDelete(e: FormEvent) {
    e.preventDefault();
    if (confirmDelete !== 'DELETE') {
      toast.error('Type DELETE to confirm');
      return;
    }
    setBusy('delete');
    try {
      await deleteAccount();
      await logout().catch(() => undefined);
      toast.success('Account deleted');
      navigate('/');
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't delete the account"));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section data-testid="page-settings" className="space-y-8">
      <h1 className="text-2xl font-bold">Settings</h1>

      <form onSubmit={onSaveName} className="space-y-3">
        <h2 className="font-semibold">Display name</h2>
        <AuthInput
          label="Display name"
          id="display_name"
          value={displayName ?? user?.display_name ?? ''}
          onChange={(e) => setDisplayName(e.target.value)}
        />
        <button
          type="submit"
          disabled={busy === 'name'}
          className="rounded-md bg-emerald-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          Save name
        </button>
      </form>

      <form onSubmit={onSetPassword} className="space-y-3">
        <h2 className="font-semibold">Password</h2>
        <p className="text-sm text-stone-600">
          Set or change the password used for email sign-in.
          {user && !user.email_verified ? ' (Google-only accounts can add one here.)' : ''}
        </p>
        <PasswordInput
          label="New password"
          id="new_password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
        <PasswordInput
          label="Confirm new password"
          id="confirm_new_password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
        <button
          type="submit"
          disabled={busy === 'password'}
          className="rounded-md bg-emerald-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          Set password
        </button>
      </form>

      <div className="space-y-3">
        <h2 className="font-semibold">Leaderboard name</h2>
        <p className="text-sm text-stone-600">
          Shown publicly on the daily leaderboards — never your real name.
        </p>
        {user?.can_set_leaderboard_name ? (
          <form onSubmit={onSaveLeaderboardName} className="space-y-3">
            <AuthInput
              label="Leaderboard name"
              id="leaderboard_name"
              value={lbName ?? user?.leaderboard_name ?? ''}
              onChange={(e) => setLbName(e.target.value)}
            />
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={busy === 'lb-name'}
                className="rounded-md bg-emerald-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                Save leaderboard name
              </button>
              <button
                type="button"
                onClick={onRandomise}
                disabled={busy === 'lb-random'}
                className="rounded-md border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 disabled:opacity-50"
              >
                Randomise
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-3">
            <p className="text-sm">
              You play as <span className="font-semibold">{user?.leaderboard_name}</span>. Google
              accounts keep a generated name — reroll it any time.
            </p>
            <button
              type="button"
              onClick={onRandomise}
              disabled={busy === 'lb-random'}
              className="rounded-md border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 disabled:opacity-50"
            >
              Randomise
            </button>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <h2 className="font-semibold">Email</h2>
        <p className="text-sm text-stone-600">
          Signed in as <span className="font-medium">{user?.email}</span>. Email changes aren't
          supported yet.
        </p>
        <label className="mt-2 flex items-center gap-3" data-testid="email-reminders-toggle">
          <input
            type="checkbox"
            checked={!user.email_opt_out}
            disabled={busy === 'reminders'}
            onChange={(e) => onToggleReminders(e.target.checked)}
          />
          <span className="text-sm text-stone-700">
            Email me reminders when I haven't played in a while
          </span>
        </label>
        <label className="mt-2 flex items-center gap-3" data-testid="results-digest-toggle">
          <input
            type="checkbox"
            checked={!user.results_digest_opt_out}
            disabled={busy === 'digest'}
            onChange={(e) => onToggleDigest(e.target.checked)}
          />
          <span className="text-sm text-stone-700">
            Email me a daily results digest (top boards on the levels I played)
          </span>
        </label>
      </div>

      <form
        onSubmit={onDelete}
        className="space-y-3 rounded-lg border border-red-200 bg-red-50 p-4"
      >
        <h2 className="font-semibold text-red-700">Delete account</h2>
        <p className="text-sm text-red-800">
          Permanently removes your account, history, and streaks. This cannot be undone. Type{' '}
          <span className="font-mono font-bold">DELETE</span> to confirm.
        </p>
        <AuthInput
          label="Confirmation"
          id="confirm_delete"
          value={confirmDelete}
          onChange={(e) => setConfirmDelete(e.target.value)}
        />
        <button
          type="submit"
          disabled={busy === 'delete'}
          className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          Delete my account
        </button>
      </form>
    </section>
  );
}
